import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../supabaseClient";
import { notify } from "../components/UI/DesignStandards/Notifications/notify";
import { validatePassword } from "../utils/validation";

/**
 * Hook to manage password update operations
 * @param {Object} params
 * @param {boolean} params.hasPassword - Whether user currently has a password
 * @param {string} params.otpCode - The OTP code entered by user
 * @param {boolean} params.otpCodeValid - Whether OTP code is valid
 * @param {Function} params.setOtpError - Function to set OTP error message
 * @param {Function} params.setHasPassword - Function to update hasPassword state
 * @param {Function} params.onSuccess - Callback when password update succeeds
 * @returns {Object} Password update state and handler
 */
export const usePasswordUpdate = ({
  hasPassword,
  otpCode,
  otpCodeValid,
  setOtpError,
  setHasPassword,
  onSuccess,
  onCodeExpired,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  /**
   * Validate password fields
   * @param {Object} formData - Form data containing password fields
   * @param {boolean} isSettingPassword - Whether user is setting (vs changing) password
   * @returns {Object} { isValid: boolean, errors: Object }
   */
  const validatePasswordFields = (formData, isSettingPassword = false) => {
    const errors = {};

    // Current password is only required if user is changing password (not setting)
    if (!isSettingPassword && !formData.currentPassword) {
      errors.currentPassword = "Current password is required";
    }

    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      errors.newPassword = passwordValidation.errors[0];
    }

    if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  /**
   * Update user password
   * @param {Object} formData - Form data containing newPassword and confirmPassword
   * @param {Function} setErrors - Function to set validation errors
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const updatePassword = async (formData, setErrors) => {
    // Determine if user is setting or changing password
    const isSettingPassword = hasPassword === false;

    // Check if OTP code is required and valid
    if (!otpCode || !otpCodeValid) {
      notify.error("Please enter a valid 6-digit verification code.");
      return false;
    }

    // Validate password fields
    const validation = validatePasswordFields(formData, isSettingPassword);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return false;
    }

    setLoading(true);
    setOtpError(null);

    try {
      // Double-check that OTP is valid before proceeding
      if (!otpCodeValid) {
        setOtpError("Please enter a valid verification code.");
        notify.error("Please enter a valid verification code.");
        setLoading(false);
        return false;
      }

      // Get the current session to send as proof to backend
      const {
        data: { session: currentSession },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError || !currentSession) {
        setOtpError("Session error. Please verify the code again.");
        notify.error("Session error. Please verify the code again.");
        setLoading(false);
        return false;
      }

      // Now call backend to update password
      // Backend will validate that the session was created via OTP verification
      const { data, error: updateError } = await supabase.functions.invoke(
        "update-password",
        {
          body: {
            otpCode: otpCode,
            newPassword: formData.newPassword,
            // Send session proof - backend can verify this session was created via OTP
            sessionProof: {
              access_token: currentSession.access_token,
              created_at: currentSession.created_at,
            },
          },
        }
      );

      // Handle edge function errors
      if (updateError) {
        let errorMessage = "Failed to update password. Please try again.";

        if (updateError.message) {
          if (
            updateError.message.includes("non-2xx") ||
            updateError.message.includes("Edge Function") ||
            updateError.message.includes("status code")
          ) {
            errorMessage = "Failed to update password. Please try again.";
          } else {
            errorMessage = updateError.message;
          }
        }

        if (data?.error) {
          errorMessage = data.error;
        }

        setOtpError(errorMessage);
        notify.error(errorMessage);
        setLoading(false);
        return false;
      }

      if (data?.error) {
        // Check if code expired or too many attempts
        if (data.codeExpired) {
          setOtpError("This code has expired. Please request a new one.");
          notify.error("This code has expired. Please request a new one.");
          // Call callback to reset code sent state (allows requesting new code)
          if (onCodeExpired) {
            onCodeExpired();
          }
        } else if (data.attemptsRemaining !== undefined) {
          const errorMsg = `${data.error} (${data.attemptsRemaining} attempts remaining)`;
          setOtpError(errorMsg);
          notify.error(errorMsg);
        } else {
          setOtpError(data.error);
          notify.error(data.error);
        }
        setLoading(false);
        return false;
      }

      // Backend validated session proof and updated password successfully
      // NOTE: Password update invalidates all sessions, so we need to create a new one
      // Sign in with the new password to create a fresh session
      const { data: signInData, error: signInError } =
        await supabase.auth.signInWithPassword({
          email: user.email,
          password: formData.newPassword,
        });

      if (signInError || !signInData.session) {
        // Password was updated but we couldn't create a new session
        console.error(
          "Password updated but failed to create new session:",
          signInError
        );
        setOtpError("Password updated successfully. Please sign in again.");
        notify.error("Password updated. Please sign in with your new password.");

        // Sign out to clear invalid session
        await supabase.auth.signOut();
        setLoading(false);
        return false;
      }

      // New session created successfully - user stays logged in
      // Mark that user now has a password
      setHasPassword(true);

      notify.success(
        isSettingPassword
          ? "Password set successfully"
          : "Password updated successfully"
      );

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      setLoading(false);
      return true;
    } catch (error) {
      // Set generic error message if not already set
      const errorMsg = "Failed to update password. Please try again.";
      setOtpError(errorMsg);
      notify.error(errorMsg);
      setLoading(false);
      return false;
    }
  };

  return {
    loading,
    updatePassword,
    validatePasswordFields,
  };
};

